import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  forwardRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import {
  PaginatedDropdownConfig,
  PaginatedDropdownController,
  PaginatedOption,
} from '@shared/utils/paginated-dropdown.util';

/**
 * PrimeNG select/multiselect backed by {@link PaginatedDropdownController}.
 * Same UX as materialName on Standard Product add (SPU): first page on open,
 * virtual scroll, load more API calls when scrolling near the list end.
 */
@Component({
  selector: 'app-paginated-select',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    MultiSelectModule,
  ],
  templateUrl: './paginated-select.component.html',
  styleUrl: './paginated-select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaginatedSelectComponent),
      multi: true,
    },
  ],
})
export class PaginatedSelectComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true })
  paginatedOptions!: PaginatedDropdownConfig<string | number | boolean>;
  @Input() multiple = false;
  @Input() placeholder = '';
  @Input() inputId?: string;
  @Input() showClear = true;
  @Input() appendTo: string | HTMLElement | null = 'body';
  @Input() styleClass = 'w-full';
  @Input() panelStyleClass?: string;
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() scrollHeight = '250px';

  value: string | number | boolean | Array<string | number> | null = null;

  options: PaginatedOption[] = [];
  filter = true;
  filterBy = 'label';
  loading = false;
  lazy = false;
  virtualScroll = false;
  virtualScrollItemSize = 38;
  hasMore = false;
  loadingMore = false;
  autoLoadMore = true;

  private controller?: PaginatedDropdownController;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.controller = new PaginatedDropdownController({
      pageSize: 100,
      ...this.paginatedOptions,
      getCurrentValue: () => this.getCurrentValueForController(),
      onOptionsChange: () => this.applyControllerProps(),
    });
    this.controller.load({ reset: true });
  }

  ngOnDestroy(): void {
    this.controller?.destroy();
  }

  writeValue(value: unknown): void {
    if (this.multiple) {
      this.value = Array.isArray(value) ? value : value != null ? [value] : [];
    } else {
      this.value = (value ?? null) as string | number | boolean | null;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onValueChange(value: unknown): void {
    this.value = value as typeof this.value;
    this.onChange(value);
    this.onTouched();
  }

  onFilter(filterValue: string): void {
    this.onFilterHandler?.(filterValue);
  }

  onLazyLoad(event: { first: number; last: number }): void {
    this.onLazyLoadHandler?.(event);
  }

  private onFilterHandler?: (filterValue: string) => void;
  private onLazyLoadHandler?: (event: { first: number; last: number }) => void;

  private applyControllerProps(): void {
    if (!this.controller) {
      return;
    }
    const props = this.controller.toSearchableDropdownProps();
    this.onFilterHandler = props.onFilter;
    this.onLazyLoadHandler = props.onLazyLoad;
    this.options = props.options;
    this.filter = props.filter;
    this.filterBy = props.filterBy;
    this.loading = props.loading;
    this.lazy = !!props.onLazyLoad;
    this.virtualScroll = props.virtualScroll;
    this.virtualScrollItemSize = props.virtualScrollItemSize;
    this.hasMore = props.hasMore;
    this.loadingMore = props.loadingMore;
    this.autoLoadMore = props.autoLoadMore;
    this.cdr.markForCheck();
  }

  private getCurrentValueForController(): string | number | undefined {
    if (this.multiple) {
      return undefined;
    }
    const v = this.value;
    if (v === null || v === undefined || v === '') {
      return undefined;
    }
    return v as string | number;
  }
}
